// Closed catalog the DoBox executor resolves a blocked action against: every integration
// the model can ever be told it is missing, each with the connections it reads to decide
// whether that's true and the fixed remedy it offers when it is. `CapabilityKey` duplicates
// the official-MCP identity list in
// apps/web/src/pages/private/Integration/constants/officialMcps.ts (23 entries today) until
// WEB-X-CAP.01 makes the web file consume this one — capabilityCatalog.spec.ts reads that
// file at test time so the two cannot drift silently.
export enum CapabilityKey {
  GMAIL = 'GMAIL',
  OUTLOOK_MAIL = 'OUTLOOK_MAIL',
  GOOGLE_CALENDAR = 'GOOGLE_CALENDAR',
  OUTLOOK_CALENDAR = 'OUTLOOK_CALENDAR',
  GOOGLE_DRIVE = 'GOOGLE_DRIVE',
  SLACK = 'SLACK',
  TEAMS = 'TEAMS',
  GOOGLE_CHAT = 'GOOGLE_CHAT',
  WHATSAPP = 'WHATSAPP',
  INSTAGRAM = 'INSTAGRAM',
  FACEBOOK = 'FACEBOOK',
  GBP = 'GBP',
  GITHUB = 'GITHUB',
  NOTION = 'NOTION',
  DROPBOX = 'DROPBOX',
  BOX = 'BOX',
  FIGMA = 'FIGMA',
  HUBSPOT = 'HUBSPOT',
  ATLASSIAN_ROVO = 'ATLASSIAN_ROVO',
  MONDAY = 'MONDAY',
  ASANA = 'ASANA',
  CLICKUP = 'CLICKUP',
  TRELLO = 'TRELLO',
  MOTION = 'MOTION',
  TAVILY = 'TAVILY',
  WINDSOR = 'WINDSOR',
  GRANOLA = 'GRANOLA',
  META_ADS = 'META_ADS',
  CANVA = 'CANVA',
  SHORTCUT = 'SHORTCUT',
  LINEAR = 'LINEAR',
  SOURCEGRAPH = 'SOURCEGRAPH',
  GLEAN = 'GLEAN',
  SALESFORCE = 'SALESFORCE',
  SNOWFLAKE = 'SNOWFLAKE',
  LOCAL_CODING_DEVICE = 'LOCAL_CODING_DEVICE',
  UNSUPPORTED_SAAS = 'UNSUPPORTED_SAAS',
}

export enum CapabilityKind {
  NATIVE = 'NATIVE',
  OFFICIAL_MCP = 'OFFICIAL_MCP',
  DEVICE = 'DEVICE',
  UNSUPPORTED = 'UNSUPPORTED',
}

export enum BlockedAction {
  READ_ITEM = 'READ_ITEM',
  UPDATE_ITEM = 'UPDATE_ITEM',
  CREATE_ITEM = 'CREATE_ITEM',
  SEND_MESSAGE = 'SEND_MESSAGE',
  SCHEDULE = 'SCHEDULE',
  SEARCH_WEB = 'SEARCH_WEB',
  DELEGATE_CODE = 'DELEGATE_CODE',
}

export enum CapabilityRemedy {
  CONNECT = 'CONNECT',
  GRANT_SCOPE = 'GRANT_SCOPE',
  RECONNECT = 'RECONNECT',
  ENABLE_TOOL = 'ENABLE_TOOL',
  RESYNC = 'RESYNC',
  CONFIGURE_BYO = 'CONFIGURE_BYO',
  PAIR_DEVICE = 'PAIR_DEVICE',
  ASK_TEAM_ADMIN = 'ASK_TEAM_ADMIN',
  REQUEST_INTEGRATION = 'REQUEST_INTEGRATION',
  UNAVAILABLE = 'UNAVAILABLE',
  /** The connection the action needs is fine — no agent the caller owns (or
   *  shares on a team) is capable of it yet. Distinct from every remedy
   *  above, which all assume a capability-key/connection problem; this one
   *  offers the in-item agent-setup wizard instead
   *  (README-criar-agente-no-item.md §4). Not resolved by
   *  resolveCapabilityRemedy.ts, which only ever sees a capability-key gap —
   *  a separate "no agent fits" check emits it. */
  SET_UP_AGENT = 'SET_UP_AGENT',
}

export enum CapabilityAlternative {
  ASK_TEAMMATE = 'ASK_TEAMMATE',
  ASK_FOR_CONTENT = 'ASK_FOR_CONTENT',
  REPLY_AT_SOURCE = 'REPLY_AT_SOURCE',
  SEND_FROM_OTHER_ACCOUNT = 'SEND_FROM_OTHER_ACCOUNT',
}

export interface CapabilityLinkMatcher {
  host: string;
  pathPrefix?: string;
}

export interface CapabilityCatalogEntry {
  kind: CapabilityKind;
  officialMcpKey?: string;
  serverUrl?: string;
  hosts: string[];
  linkMatchers: CapabilityLinkMatcher[];
  // Populated by the slice that wires tool-call classification
  // (W-X-CAP.01 / API-CAP.01) — a static per-key tool list can't cover an MCP server's
  // dynamically discovered tools, and is left empty here rather than guessed.
  actionTools: Partial<Record<BlockedAction, string[]>>;
  readAllowlist: string[];
}

type CapabilityCatalogEntryInput = Omit<CapabilityCatalogEntry, 'hosts'> & {
  linkMatchers: CapabilityLinkMatcher[];
};

// `hosts` is always derived from `linkMatchers` — one source of truth, so the two can never
// drift apart the way a hand-duplicated second list would.
const defineCapability = (
  input: CapabilityCatalogEntryInput,
): CapabilityCatalogEntry => ({
  ...input,
  hosts: input.linkMatchers.map((matcher) => matcher.host),
});

export const CAPABILITY_CATALOG = {
  [CapabilityKey.GMAIL]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'mail.google.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.OUTLOOK_MAIL]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [
      { host: 'outlook.office.com', pathPrefix: '/mail' },
      { host: 'outlook.office365.com', pathPrefix: '/mail' },
      { host: 'outlook.live.com', pathPrefix: '/mail' },
    ],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GOOGLE_CALENDAR]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'calendar.google.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.OUTLOOK_CALENDAR]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [
      { host: 'outlook.office.com', pathPrefix: '/calendar' },
      { host: 'outlook.office365.com', pathPrefix: '/calendar' },
    ],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GOOGLE_DRIVE]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'drive.google.com' }, { host: 'docs.google.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.SLACK]: defineCapability({
    kind: CapabilityKind.NATIVE,
    // Every workspace is its own subdomain of slack.com — the wildcard already covers
    // app.slack.com, so there is no separate bare-host entry.
    linkMatchers: [{ host: '*.slack.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.TEAMS]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'teams.microsoft.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GOOGLE_CHAT]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'chat.google.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.WHATSAPP]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'wa.me' }, { host: 'web.whatsapp.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.INSTAGRAM]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'instagram.com' }, { host: 'www.instagram.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.FACEBOOK]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [
      { host: 'facebook.com' },
      { host: 'www.facebook.com' },
      { host: 'fb.com' },
    ],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GBP]: defineCapability({
    kind: CapabilityKind.NATIVE,
    linkMatchers: [{ host: 'business.google.com' }, { host: '*.g.page' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GITHUB]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'github',
    serverUrl: 'https://api.githubcopilot.com/mcp/',
    linkMatchers: [{ host: 'github.com' }],
    actionTools: {},
    // Read-only, decision 29 — none of these creates, updates, deletes, merges, closes or
    // archives anything.
    readAllowlist: ['search_issues', 'get_issue', 'get_file_contents'],
  }),
  [CapabilityKey.NOTION]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'notion',
    serverUrl: 'https://mcp.notion.com/mcp',
    linkMatchers: [{ host: 'notion.so' }, { host: '*.notion.site' }],
    actionTools: {},
    readAllowlist: ['search', 'fetch'],
  }),
  [CapabilityKey.DROPBOX]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'dropbox',
    serverUrl: 'https://mcp.dropbox.com/mcp',
    linkMatchers: [{ host: 'dropbox.com' }, { host: 'www.dropbox.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.BOX]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'box',
    serverUrl: 'https://mcp.box.com',
    linkMatchers: [{ host: 'app.box.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.FIGMA]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'figma',
    serverUrl: 'https://mcp.figma.com/mcp',
    linkMatchers: [{ host: 'figma.com' }, { host: 'www.figma.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.HUBSPOT]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'hubspot',
    serverUrl: 'https://mcp.hubspot.com',
    linkMatchers: [{ host: 'app.hubspot.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.ATLASSIAN_ROVO]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'atlassian-rovo',
    serverUrl: 'https://mcp.atlassian.com/v1/mcp',
    // Jira/Confluence Cloud sites are per-tenant subdomains of atlassian.net — the wildcard
    // is what the Jira-ticket-link example (acme.atlassian.net/browse/PROJ-142) matches on.
    linkMatchers: [{ host: '*.atlassian.net' }],
    actionTools: {},
    readAllowlist: [
      'getJiraIssue',
      'searchConfluencePages',
      'getConfluencePage',
    ],
  }),
  [CapabilityKey.MONDAY]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'monday',
    serverUrl: 'https://mcp.monday.com/mcp',
    linkMatchers: [{ host: '*.monday.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.ASANA]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'asana',
    serverUrl: 'https://mcp.asana.com/sse',
    linkMatchers: [{ host: 'app.asana.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.CLICKUP]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'clickup',
    serverUrl: 'https://mcp.clickup.com/mcp',
    linkMatchers: [{ host: 'app.clickup.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.TRELLO]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'trello',
    serverUrl: 'https://mcp.trello.com/v1',
    linkMatchers: [{ host: 'trello.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.MOTION]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'motion',
    serverUrl: 'https://projects.motionapp.com/mcp',
    linkMatchers: [{ host: 'app.usemotion.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.TAVILY]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'tavily',
    serverUrl: 'https://mcp.tavily.com/mcp/',
    linkMatchers: [{ host: 'tavily.com' }],
    actionTools: {},
    readAllowlist: ['tavily_search', 'tavily_extract'],
  }),
  [CapabilityKey.WINDSOR]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'windsor',
    serverUrl: 'https://mcp.windsor.ai/',
    linkMatchers: [{ host: 'windsor.ai' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GRANOLA]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'granola',
    serverUrl: 'https://mcp.granola.ai/mcp',
    linkMatchers: [{ host: 'app.granola.ai' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.META_ADS]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'metaAds',
    serverUrl: 'https://mcp.facebook.com/ads',
    linkMatchers: [],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.CANVA]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'canva',
    serverUrl: 'https://mcp.canva.com/mcp',
    linkMatchers: [{ host: 'canva.com' }, { host: 'www.canva.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.SHORTCUT]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'shortcut',
    serverUrl: 'https://mcp.shortcut.com/mcp',
    linkMatchers: [{ host: 'app.shortcut.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.LINEAR]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'linear',
    serverUrl: 'https://mcp.linear.app/mcp',
    linkMatchers: [{ host: 'linear.app' }],
    actionTools: {},
    readAllowlist: ['list_issues', 'get_issue', 'search_issues'],
  }),
  [CapabilityKey.SOURCEGRAPH]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'sourcegraph',
    serverUrl: 'https://sourcegraph.com/.api/mcp/v1',
    linkMatchers: [{ host: 'sourcegraph.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.GLEAN]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'glean',
    // Glean has no fixed public serverUrl — deployment is per-tenant and configured by the
    // connecting user.
    serverUrl: '',
    linkMatchers: [{ host: '*.glean.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.SALESFORCE]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'salesforce',
    serverUrl: '',
    linkMatchers: [
      { host: '*.salesforce.com' },
      { host: '*.lightning.force.com' },
    ],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.SNOWFLAKE]: defineCapability({
    kind: CapabilityKind.OFFICIAL_MCP,
    officialMcpKey: 'snowflake',
    serverUrl: '',
    linkMatchers: [{ host: '*.snowflakecomputing.com' }],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.LOCAL_CODING_DEVICE]: defineCapability({
    kind: CapabilityKind.DEVICE,
    linkMatchers: [],
    actionTools: {},
    readAllowlist: [],
  }),
  [CapabilityKey.UNSUPPORTED_SAAS]: defineCapability({
    kind: CapabilityKind.UNSUPPORTED,
    linkMatchers: [],
    actionTools: {},
    readAllowlist: [],
  }),
} satisfies Record<CapabilityKey, CapabilityCatalogEntry>;

const hostMatchesPattern = (hostname: string, pattern: string): boolean => {
  if (pattern.startsWith('*.')) {
    // Keep the leading dot in the suffix so a label boundary is enforced — otherwise
    // 'evilatlassian.net' would match '*.atlassian.net' by bare .endsWith('atlassian.net').
    const suffix = pattern.slice(1);
    return hostname.length > suffix.length && hostname.endsWith(suffix);
  }
  return hostname === pattern;
};

const MCP_SERVER_HOST_TO_CAPABILITY_KEY: ReadonlyMap<string, CapabilityKey> =
  new Map(
    (Object.keys(CAPABILITY_CATALOG) as CapabilityKey[])
      .map((key): [string, CapabilityKey] | null => {
        const { serverUrl } = CAPABILITY_CATALOG[key];
        if (!serverUrl) {
          return null;
        }
        try {
          return [new URL(serverUrl).hostname.toLowerCase(), key];
        } catch {
          return null;
        }
      })
      .filter((entry): entry is [string, CapabilityKey] => entry !== null),
  );

export interface MatchedCapability {
  capabilityKey: CapabilityKey;
  ref: string;
}

// Detects which capability a URL found in DoBox content belongs to, by comparing
// `new URL(url).hostname` against the catalog's `linkMatchers` — exact equality for a plain
// host, suffix-on-a-label-boundary for a '*.' wildcard. An unknown host is ignored (returns
// null), never guessed at. `ref` is the matched link's path + query, left for the caller to
// parse further (e.g. extracting a Jira issue key) — this lib stays pure and has no
// per-provider parsing logic.
export const matchCapabilityByUrl = (url: string): MatchedCapability | null => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const hostname = parsed.hostname.toLowerCase();
  for (const key of Object.keys(CAPABILITY_CATALOG) as CapabilityKey[]) {
    const matcher = CAPABILITY_CATALOG[key].linkMatchers.find(
      (candidate) =>
        hostMatchesPattern(hostname, candidate.host) &&
        (!candidate.pathPrefix ||
          parsed.pathname.startsWith(candidate.pathPrefix)),
    );
    if (matcher) {
      return { capabilityKey: key, ref: `${parsed.pathname}${parsed.search}` };
    }
  }
  return null;
};

// Maps an installed MCP connection's own `serverUrl` back to the capability it satisfies —
// distinct from `matchCapabilityByUrl`, which matches a content link's *public* host (e.g.
// github.com), not the MCP endpoint host (e.g. api.githubcopilot.com).
export const matchCapabilityByMcpServerUrl = (
  serverUrl: string,
): CapabilityKey | null => {
  let parsed: URL;
  try {
    parsed = new URL(serverUrl);
  } catch {
    return null;
  }
  return (
    MCP_SERVER_HOST_TO_CAPABILITY_KEY.get(parsed.hostname.toLowerCase()) ?? null
  );
};

export const isCatalogReadTool = (
  key: CapabilityKey,
  toolName: string,
): boolean => CAPABILITY_CATALOG[key].readAllowlist.includes(toolName);
